import React, { useContext, useEffect, useState } from "react";
import { Button } from "@cmsgov/ds-medicare-gov";
import Tooltip from "react-tooltip-lite";
import { PlanDetailList, ScrollTable } from "../../components";
import { AppStore } from "../../app/store";
import { makePlanUrlPartForApi, useTranslate, logError } from "../../helpers";
import {
  Plan,
  DrugInfo,
  DrugCostInfo,
  DrugFootnote,
  PlanDetailsSection,
} from "../../@types";
import { getOtherDrugInfo } from "../../api";
import { FormattedMessage } from "react-intl-phraseapp";
import { displayDrug } from "../../helpers/prescriptionHelpers";

interface OtherDrugInfoProps {
  plan: Plan;
  drugCostInfo: DrugCostInfo;
}

const OtherDrugInfo: React.FunctionComponent<OtherDrugInfoProps> = ({
  plan,
  drugCostInfo,
}) => {
  const {
    state: { prescriptions },
  } = useContext(AppStore.AppContext);
  const [footnotes, setFootnotes] = useState<
    { ndcs: string[]; text: string; type: DrugFootnote }[]
  >([]);
  const t = useTranslate();
  const [drugInfo, setDrugInfo] = useState<DrugInfo[]>([]);

  useEffect(() => {
    const ndcs = prescriptions.map(p => p.ndc);

    if (ndcs.length) {
      const planUrl = makePlanUrlPartForApi(plan);
      getOtherDrugInfo(ndcs, planUrl).then(
        res => {
          if (res) setDrugInfo(res);
          else throw "API failure getting OtherDrugInfo";
        },
        e => {
          logError("Failed to get other drug information (OtherDrugInfo)", e);
        }
      );
    }
  }, []);

  useEffect(() => {
    const { restrictions } = drugCostInfo;

    const conditions = restrictions.filter(r => !!r.conditions.length);
    const opioids = restrictions.filter(r => r.opioid);
    const homeInfusions = restrictions.filter(r => r.home_infusion);
    const limitedDistros = restrictions.filter(r => r.limited_distribution);
    const vaccines = restrictions.filter(r => r.vaccine);

    const notes: { ndcs: string[]; text: string; type: DrugFootnote }[] = [];

    conditions.forEach(c => {
      notes.push({
        ndcs: [c.ndc],
        text:
          t("plan_details.drug_restrictions") + " " + c.conditions.join("; "),
        type: DrugFootnote.CONDITION,
      });
    });

    if (opioids.length) {
      notes.push({
        ndcs: opioids.map(o => o.ndc),
        text: t("plan_details.opioid_footnote"),
        type: DrugFootnote.OPIOID,
      });
    }

    if (homeInfusions.length) {
      notes.push({
        ndcs: homeInfusions.map(h => h.ndc),
        text: t("drug_footnote.home_infusion"),
        type: DrugFootnote.HOME_INFUSION,
      });
    }

    if (limitedDistros.length) {
      notes.push({
        ndcs: limitedDistros.map(l => l.ndc),
        text: t("drug_footnote.limited_distribution"),
        type: DrugFootnote.LIMITED_DISTRIBUTION,
      });
    }

    if (vaccines.length) {
      notes.push({
        ndcs: vaccines.map(l => l.ndc),
        text: t("drug_footnote.vaccine"),
        type: DrugFootnote.VACCINE,
      });
    }

    setFootnotes(notes);
  }, [drugCostInfo]);

  const showFootnoteSuperscripts = (ndc: string): JSX.Element => {
    const superscripts = footnotes
      .map((f, i) => (f.ndcs.includes(ndc) ? i + 1 : ""))
      .filter(f => !!f);

    return <sup className="ds-u-color--gray">{superscripts.join(",")}</sup>;
  };

  if (drugInfo.length > 0) {
    return (
      <PlanDetailList
        id={PlanDetailsSection.OTHER_DRUG_INFO}
        title={t("plan_terms.other_drug_information")}
      >
        <div className="DrugCostsTables__tableContainer">
          <ScrollTable className="PlanDetailTable">
            <thead>
              <tr>
                <th scope="col">{t("plan_terms.drug_list.selected_drugs")}</th>
                <th scope="col">{t("plan_terms.tier")}</th>
                <th scope="col">{t("plan_terms.prior_authorization")}</th>
                <th scope="col">{t("plan_terms.quantity_limits")}</th>
                <th scope="col">{t("plan_terms.step_therapy")}</th>
              </tr>
            </thead>
            <tbody>
              {prescriptions.map(prescription => {
                const drug = drugInfo.find(d => d.ndc === prescription.ndc);
                if (!drug) {
                  return null;
                }

                return (
                  <tr key={drug.ndc}>
                    <td>
                      {displayDrug(t, prescription)}{" "}
                      {showFootnoteSuperscripts(drug.ndc)}
                    </td>
                    <td>
                      {drug.tier > 0 ? (
                        <FormattedMessage
                          id="plan_terms.tier-number"
                          tagName="span"
                          values={{ tier: drug.tier }}
                        />
                      ) : (
                        t("plan_terms.not_covered")
                      )}
                    </td>
                    <td>{drug.prior_auth ? t("yes") : ""}</td>
                    <td>
                      <Tooltip
                        content={
                          <div
                            className="OtherDrugInfo__tooltipContent"
                            id="quantity-limits-tooltip"
                            role="tooltip"
                          >
                            <span className="ds-u-font-weight--bold">
                              {t("plan_terms.quantity_limits_amount")}
                            </span>

                            <span className="ds-u-margin-bottom--1">
                              {drug.quantity_limit
                                ? drug.quantity_limit_amount
                                : ""}
                            </span>
                            <span className="ds-u-font-weight--bold">
                              {t("plan_terms.quantity_limits_frequency")}
                            </span>
                            <span>
                              {drug.quantity_limit
                                ? `${drug.quantity_limit_days} ${t("days")}`
                                : ""}
                            </span>
                          </div>
                        }
                        tagName="span"
                        className="target OtherDrugInfo__tooltip"
                        direction="up"
                        forceDirection
                        eventOn="onClick"
                        eventOff="onMouseOut"
                        distance={20}
                        tipContentHover
                      >
                        {drug.quantity_limit ? (
                          <Button
                            variation="transparent"
                            aria-labelledby="quantity-limits-tooltip"
                            className="Tooltip__trigger"
                          >
                            {t("yes")}
                          </Button>
                        ) : (
                          ""
                        )}
                      </Tooltip>
                    </td>
                    <td>{drug.step_therapy ? t("yes") : ""}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="ds-u-fill--white">
                <td colSpan={5} className="ds-u-border-y--0">
                  <ul className="ds-c-list ds-c-list--bare ds-u-color--gray">
                    {footnotes.map((footnote, i) => (
                      <li key={i}>
                        <sup>{i + 1} </sup>
                        <span className={`e2e-other-footnote-${footnote.type}`}>
                          {footnote.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </td>
              </tr>
            </tfoot>
          </ScrollTable>
        </div>
      </PlanDetailList>
    );
  }
  return null;
};

export default OtherDrugInfo;
